import { useState } from 'react'
import {
  PaymentAmountSection,
  type PaymentType,
} from '@/src/app/main/(tabs)/card/card-management/_common/paymentAmountSection'
import {
  DateSectionSection,
  type DateType,
} from '@/src/app/main/(tabs)/card/card-management/_common/dateSectionSection'
import { Button } from '@/src/common/components/Button'
import { CardPaymentsStore as Store } from '@/src/experiences/card-payments/store'
import { ScrollView } from 'react-native'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { z } from 'zod/v4'
import { usePaymentScreen } from '@/src/experiences/card-payments/hooks/usePaymentScreen'
import { CardPaymentsE2E } from '@/src/experiences/card-payments/e2e'
import { withBalance } from '@/src/experiences/card-payments/screens/MakePayment/Balance'

export function useOnetimePayScheduleCardStackScreenProps() {
  return usePaymentScreen(
    'oneTimePayment/index',
    'Select Payment',
    '/main/card/card-management/payment',
  )
}

const PaymentSchema = z
  .object({
    paymentAmount: z.coerce.number().gt(0, { message: 'Payment amount must be more than $0.00.' }),
    currentBalanceValue: z.coerce
      .number()
      .gt(0, { message: 'Current balance must be more than $0.00.' }),
  })
  // Custom refinement: payment amount should not exceed the current balance.
  .refine((data) => data.paymentAmount <= data.currentBalanceValue, {
    message: 'Select an amount that does not exceed your current balance to proceed.',
    path: ['paymentAmount'],
  })

const OnetimePaySchedule = withBalance(
  ({ minimumPayment, statementBalance, currentBalance, statementDueOn }) => {
    const Alert = useAlert(OnetimePaySchedule.name)
    const oneTimePaymentStore = Store.useOneTimePayment()
    const [paymentAmount, setPaymentAmount] = useState(oneTimePaymentStore.paymentAmount)
    const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentType>(
      () => oneTimePaymentStore.paymentType || 'Minimum Payment',
    )

    const [dateType, setDateType] = useState<DateType>(
      () => oneTimePaymentStore.paymentDateType ?? 'Today',
    )
    const statementDueOnDate = statementDueOn?.value ? new Date(statementDueOn.value) : undefined
    const [date, setDate] = useState(() => {
      switch (dateType) {
        case 'Today':
          return new Date()
        case 'Due Date':
          return statementDueOnDate
        case 'Other Date':
          return oneTimePaymentStore.paymentDate
            ? new Date(oneTimePaymentStore.paymentDate)
            : undefined
      }
    })

    const { Modal, routerPush } = useOnetimePayScheduleCardStackScreenProps()

    return Modal({
      children: (
        <ScrollView contentContainerStyle={{ paddingBottom: 50 }}>
          <PaymentAmountSection
            minimumPayment={minimumPayment?.value?.toString()}
            statementBalance={statementBalance?.value.toString()}
            currentBalance={currentBalance?.value.toString()}
            onAmountChange={setPaymentAmount}
            paymentAmount={paymentAmount}
            selectedPaymentType={selectedPaymentType}
            onPaymentTypeSelectionChange={setSelectedPaymentType}
          />
          <DateSectionSection
            dueDate={statementDueOnDate}
            dateType={dateType}
            setDateType={setDateType}
            date={date}
            setDate={setDate}
          />
          <Button
            title="Continue"
            testID={CardPaymentsE2E.Ids.Press.Continue}
            disabled={!paymentAmount || !date}
            onPress={() => {
              const result = PaymentSchema.safeParse({
                paymentAmount,
                currentBalanceValue: currentBalance?.value,
              } satisfies z.input<typeof PaymentSchema>)

              if (!result.success) {
                Alert.alert(
                  'Unable to Schedule Payment',
                  Object.values(z.flattenError(result.error).fieldErrors).flat().join('\n'),
                )
                return
              }

              routerPush('/main/(tabs)/card/card-management/payment/oneTimePayment/fundingSource')
              oneTimePaymentStore.setPaymentAmount(paymentAmount)
              oneTimePaymentStore.setPaymentType(selectedPaymentType)
              oneTimePaymentStore.setPaymentDateType(dateType)
              // Need to convert the date to ISO string to store it in the store
              // see https://github.com/pmndrs/zustand/discussions/1720
              oneTimePaymentStore.setPaymentDate(date?.toISOString())
            }}
          />
        </ScrollView>
      ),
    })
  },
)

export const OnetimePayScheduleScreen = {
  name: 'auto-pay-schedule',
  Screen: OnetimePaySchedule,
} as const
